/**
 * Hook de récupération de la météo pour une localisation (lat/lon).
 * Gère les états chargement / erreur et l'horodatage de la dernière mise à jour.
 */
import { useEffect, useState } from "react";
import { fetchWeather, WeatherData } from "../../../../api/weather";

type UseWeatherResult = {
  data: WeatherData | null;
  loading: boolean;
  error: boolean;
  updatedAt: Date | null;
};

export function useWeather(
  latitude?: number,
  longitude?: number
): UseWeatherResult {
  const [data, setData] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (latitude === undefined || longitude === undefined) return;

    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError(false);

    fetchWeather(latitude, longitude, controller.signal)
      .then((result) => {
        if (!active) return;
        setData(result.data);
        // Horodatage du fetch réel (peut venir du cache) et non de la lecture.
        setUpdatedAt(new Date(result.fetchedAt));
      })
      .catch((err) => {
        if (active && err.name !== "AbortError") setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [latitude, longitude]);

  return { data, loading, error, updatedAt };
}
